'use client';

import { Check, Download, Edit2, FileIcon, Maximize2, Trash2, X } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { cn } from '../lib/utils';
import type { EditorAttachment } from '../types/files';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { VisuallyHidden } from './visually-hidden';

export interface AttachmentListProps {
  attachments?: EditorAttachment[];
  onRemove?: (index: number) => void;
  onRename?: (index: number, newName: string) => void;
  className?: string;
}

export function AttachmentList({
  attachments = [],
  onRemove,
  onRename,
  className,
}: AttachmentListProps) {
  if (attachments.length === 0) return null;

  return (
    <div
      className={cn('flex flex-wrap gap-3 px-3 py-3 border-t border-border bg-muted/5', className)}
    >
      {attachments.map((attachment, i) => (
        <AttachmentItem
          key={attachment.type === 'local' ? attachment.id : `${attachment.fileId}-${i}`}
          attachment={attachment}
          onRemove={onRemove ? () => onRemove(i) : undefined}
          onRename={onRename ? (newName) => onRename(i, newName) : undefined}
        />
      ))}
    </div>
  );
}

function AttachmentItem({
  attachment,
  onRemove,
  onRename,
}: {
  attachment: EditorAttachment;
  onRemove?: () => void;
  onRename?: (newName: string) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const localObjectUrl = useMemo(() => {
    if (attachment.type === 'local' && attachment.file.type.startsWith('image/')) {
      return URL.createObjectURL(attachment.file);
    }
    return null;
  }, [attachment]);

  useEffect(() => {
    return () => {
      if (localObjectUrl) URL.revokeObjectURL(localObjectUrl);
    };
  }, [localObjectUrl]);

  const previewUri =
    localObjectUrl ??
    (attachment.type === 'remote' && attachment.fileType.startsWith('image/')
      ? attachment.viewUrl || null
      : null);

  const fileName = attachment.type === 'local' ? attachment.file.name : attachment.fileName;
  const displayName =
    attachment.type === 'remote' && attachment.label ? attachment.label : fileName;
  const isImage = (
    attachment.type === 'local' ? attachment.file.type : attachment.fileType
  ).startsWith('image/');

  const handleDownload = () => {
    const a = document.createElement('a');
    if (attachment.type === 'local') {
      const url = URL.createObjectURL(attachment.file);
      a.href = url;
      a.download = attachment.file.name;
      a.click();
      URL.revokeObjectURL(url);
    } else if (attachment.viewUrl) {
      a.href = attachment.viewUrl;

      // Ensure extension is preserved in download name
      let downloadName = displayName;
      const extension = fileName.split('.').pop();
      if (extension && !downloadName.toLowerCase().endsWith(`.${extension.toLowerCase()}`)) {
        downloadName = `${downloadName}.${extension}`;
      }

      a.download = downloadName;
      a.target = '_blank';
      a.click();
    }
  };

  const handleRenameSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (newName.trim() && newName !== displayName) {
      onRename?.(newName.trim());
    }
    setIsEditing(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <div className="group relative flex items-center justify-center border border-border rounded-lg bg-background shadow-sm hover:border-primary/50 transition-colors w-14 h-14 overflow-visible shrink-0">
        <DialogTrigger asChild>
          <button
            type="button"
            className="w-full h-full p-0 border-none bg-transparent group/btn relative overflow-hidden rounded-md"
          >
            {previewUri ? (
              <img src={previewUri} alt={displayName} className="w-full h-full object-cover" />
            ) : (
              <div className="flex flex-col items-center justify-center gap-0.5 p-1 text-muted-foreground/60 w-full h-full">
                <FileIcon className="size-5 shrink-0" />
                <span className="text-[8px] w-full text-center truncate px-1 font-medium leading-tight">
                  {displayName.split('.').pop()?.toUpperCase()}
                </span>
              </div>
            )}
            <div className="absolute inset-0 bg-black/0 group-hover/btn:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover/btn:opacity-100">
              <div className="bg-white/20 backdrop-blur-md rounded-full p-1.5 text-white">
                <Maximize2 className="size-3" />
              </div>
            </div>
          </button>
        </DialogTrigger>

        {onRemove && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="absolute -top-1.5 -right-1.5 bg-background border border-border text-muted-foreground hover:bg-destructive hover:text-destructive-foreground hover:border-destructive rounded-full p-0.5 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-all shadow-sm z-10"
            title="Remove attachment"
          >
            <X className="size-3" />
          </button>
        )}

        {attachment.type === 'remote' && (
          <div className="absolute -bottom-1 -left-1 size-3 bg-primary rounded-full border border-background flex items-center justify-center shadow-sm">
            <div className="size-1 bg-white rounded-full" />
          </div>
        )}
      </div>

      <DialogContent className="max-w-[90vw] sm:max-w-[600px] p-0 overflow-hidden gap-0">
        <DialogHeader className="p-4 border-b border-border bg-muted/30">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <form onSubmit={handleRenameSubmit} className="flex items-center gap-2">
                  <Input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="h-8 py-0 px-2 text-sm focus-visible:ring-1"
                    autoFocus
                    onBlur={() => handleRenameSubmit()}
                  />
                  <Button type="submit" size="sm" variant="ghost" className="h-8 w-8 p-0">
                    <Check className="size-4 text-green-500" />
                  </Button>
                </form>
              ) : (
                <div className="flex items-center gap-2 group/title">
                  <DialogTitle className="text-sm font-semibold truncate max-w-[300px]">
                    {displayName}
                  </DialogTitle>
                  {onRename && (
                    <button
                      onClick={() => {
                        setNewName(displayName);
                        setIsEditing(true);
                      }}
                      className="opacity-0 group-hover/title:opacity-100 transition-opacity p-1 hover:bg-muted rounded"
                    >
                      <Edit2 className="size-3 text-muted-foreground" />
                    </button>
                  )}
                </div>
              )}
              <DialogDescription className="text-[10px] text-muted-foreground uppercase tracking-tight font-medium mt-0.5">
                {attachment.type === 'local' ? 'Local File' : 'Stored in Cloud'} •{' '}
                {attachment.type === 'local'
                  ? `${Math.round(attachment.file.size / 1024)} KB`
                  : attachment.fileType}
              </DialogDescription>
            </div>
            <VisuallyHidden>
              <DialogTitle>{displayName}</DialogTitle>
              <DialogDescription>Viewing file {displayName}</DialogDescription>
            </VisuallyHidden>
          </div>
        </DialogHeader>

        <div className="flex items-center justify-center bg-muted/10 p-6 min-h-[300px] max-h-[70vh] overflow-auto">
          {isImage && previewUri ? (
            <img
              src={previewUri}
              alt={displayName}
              className="max-w-full max-h-full object-contain rounded-lg shadow-lg border border-border"
            />
          ) : (
            <div className="flex flex-col items-center gap-4 text-muted-foreground py-12">
              <div className="size-20 rounded-2xl bg-muted flex items-center justify-center border border-border shadow-inner">
                <FileIcon className="size-10" />
              </div>
              <div className="text-center space-y-1">
                <p className="text-sm font-medium text-foreground">No preview available</p>
                <p className="text-xs">You can still download this file to view it.</p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="p-3 border-t border-border bg-muted/30 sm:justify-between flex-row items-center gap-2">
          <div className="flex items-center gap-2">
            {onRemove && (
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-2 h-9"
                onClick={() => {
                  onRemove();
                  setIsOpen(false);
                }}
              >
                <Trash2 className="size-4" />
                <span className="hidden sm:inline">Delete File</span>
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-2 h-9"
              onClick={() => setIsOpen(false)}
            >
              Close
            </Button>
            <Button size="sm" className="gap-2 h-9 shadow-sm" onClick={handleDownload}>
              <Download className="size-4" />
              Download
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
